import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ref, uploadString, getDownloadURL } from "@firebase/storage";
import { storage } from "../../main-component/Firebase";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { Editor } from "@tinymce/tinymce-react";
import EventConfirmationFields from "../EventConfirmationFields";
import { resolveEventConfirmationCopy } from "../../utils/eventConfirmationCopy";
import "react-day-picker/style.css";
import "../AdminCreateEventForm/calendar-styles.css";
import "./EventEditModal.css";

const HOURS = [
  "08:00", "09:00", "10:00", "11:00", "12:00",
  "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00", "21:00", "22:00"
];

/* ── Custom hour picker (identical to AdminCreateEventForm) ── */
const HourSelect = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="hour-select" ref={wrapRef}>
      <button
        type="button"
        className={`hour-select__trigger${open ? " hour-select__trigger--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? "hour-select__value" : "hour-select__placeholder"}>
          {value || placeholder}
        </span>
        <svg
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          className={`hour-select__chevron${open ? " hour-select__chevron--open" : ""}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <ul className="hour-select__list" role="listbox">
          {HOURS.map((h) => (
            <li
              key={h}
              role="option"
              aria-selected={value === h}
              className={`hour-select__option${value === h ? " hour-select__option--selected" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(h); setOpen(false); }}
            >
              {h}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const EventEditModal = ({ event, onClose, onSaved }) => {
  const initialConfirmationCopy = resolveEventConfirmationCopy(event);
  const [title, setTitle] = useState(event.title || "");
  const [author, setAuthor] = useState(event.author || "");
  const [pageName, setPageName] = useState(event.id || event.docId || "");
  const originalPageName = event.id || event.docId || "";
  const [location, setLocation] = useState(event.location || "");
  const [text, setText] = useState(event.text || "");
  const [eventType, setEventType] = useState(event.eventType || "Fizic");
  const [coverImage, setCoverImage] = useState(event.coverImage || "");
  const [base64URL, setBase64URL] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [startHour, setStartHour] = useState(event.startHour || "");
  const [endHour, setEndHour] = useState(event.endHour || "");
  const [confirmationPretitle, setConfirmationPretitle] = useState(
    initialConfirmationCopy.pretitle
  );
  const [confirmationTitle, setConfirmationTitle] = useState(
    initialConfirmationCopy.title
  );
  const [confirmationText, setConfirmationText] = useState(
    initialConfirmationCopy.text
  );
  const [dateRange, setDateRange] = useState(() => {
    const from = event.dateFrom ? new Date(event.dateFrom) : undefined;
    const to = event.dateTo ? new Date(event.dateTo) : undefined;
    return from ? { from, to: to || from } : undefined;
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputRef = useRef(null);
  const editorRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const getBase64 = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });

  const uploadImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImagePreview(file);
    getBase64(file).then((result) => setBase64URL(result));
  };

  const buildTimeInterval = () => {
    let interval = "";
    if (dateRange?.from) {
      interval += format(dateRange.from, "d MMMM yyyy", { locale: ro });
      if (dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime()) {
        interval += " - " + format(dateRange.to, "d MMMM yyyy", { locale: ro });
      }
    }
    if (startHour && endHour) {
      interval += `, ${startHour} - ${endHour}`;
    }
    return interval;
  };

  const getDateLabel = () => {
    if (!dateRange?.from) return "";
    let label = format(dateRange.from, "d MMM yyyy", { locale: ro });
    if (dateRange.to && dateRange.to.getTime() !== dateRange.from.getTime()) {
      label += " → " + format(dateRange.to, "d MMM yyyy", { locale: ro });
    }
    return label;
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    setSaving(true);

    try {
      let imageUrl = coverImage;

      if (base64URL) {
        const imageRef = ref(storage, `campaign/${pageName}`);
        await uploadString(imageRef, base64URL, "data_url");
        imageUrl = await getDownloadURL(imageRef);
      }

      const timeInterval = buildTimeInterval();
      const id = event.id || event.docId;

      const body = {
        title,
        author,
        location,
        text,
        eventType,
        coverImage: imageUrl,
        timeInterval,
        dateFrom: dateRange?.from ? dateRange.from.toISOString() : "",
        dateTo: dateRange?.to ? dateRange.to.toISOString() : "",
        startHour,
        endHour,
        confirmationPretitle,
        confirmationTitle,
        confirmationText,
      };

      // If the admin changed the page name, include newPageName so the backend
      // can copy the document to a new ID and delete the old one.
      const trimmedPageName = pageName.trim().toLowerCase().replace(/\s+/g, "-");
      if (trimmedPageName && trimmedPageName !== originalPageName) {
        body.newPageName = trimmedPageName;
      }

      await axios.put(
        `${process.env.REACT_APP_API_BASE}/campaign/put?id=${id}`,
        body,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );

      toast.success("Eveniment actualizat cu succes!");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error("Error updating event:", err);
      toast.error("Eroare la actualizarea evenimentului");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="event-edit-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="event-edit-modal" ref={modalRef}>
        <div className="event-edit-modal__header">
          <h3>Editează Eveniment</h3>
          <button className="event-edit-modal__close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="event-edit-modal__body">
          {/* Title - full width */}
          <div className="row">
            <div className="col-lg-12 col-md-12 col-12">
              <div className="form-field">
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Titlu"
                />
              </div>
            </div>

            {/* Author + Page Name */}
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Autor"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field">
                <input
                  type="text"
                  value={pageName}
                  onChange={(e) => setPageName(e.target.value)}
                  placeholder="Nume pagină (ex: the-inner-cluj-2026)"
                />
                {pageName.trim() !== originalPageName && pageName.trim() && (
                  <small style={{ color: "#904d71", fontFamily: "Montserrat,sans-serif", fontSize: "11px", marginTop: 4 }}>
                    URL-ul evenimentului va fi schimbat la /evenimente/{pageName.trim().toLowerCase().replace(/\s+/g, "-")}
                  </small>
                )}
              </div>
            </div>

            {/* Event Type */}
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontFamily: "Montserrat, sans-serif", fontSize: "12px", fontWeight: 600, color: "#555", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                  Tip Eveniment
                </label>
                <div style={{ display: "flex", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", width: "fit-content" }}>
                  <button
                    type="button"
                    onClick={() => setEventType("Fizic")}
                    style={{
                      padding: "8px 20px",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: eventType === "Fizic" ? "#904d71" : "#fff",
                      color: eventType === "Fizic" ? "#fff" : "#666",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Fizic
                  </button>
                  <button
                    type="button"
                    onClick={() => setEventType("Online")}
                    style={{
                      padding: "8px 20px",
                      border: "none",
                      borderLeft: "1px solid #e0e0e0",
                      cursor: "pointer",
                      fontFamily: "Montserrat, sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      background: eventType === "Online" ? "#904d71" : "#fff",
                      color: eventType === "Online" ? "#fff" : "#666",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Online
                  </button>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="col-lg-12 col-md-12 col-12">
              <div className="form-field">
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Locație (ex: Cinema Florin Piersic, Cluj)"
                />
              </div>
            </div>

            {/* Visual Calendar */}
            <div className="col-lg-12 col-md-12 col-12">
              <div className="form-field">
                <div className="admin-calendar-wrapper">
                  <button
                    type="button"
                    className="admin-calendar-trigger"
                    onClick={() => setShowCalendar(!showCalendar)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span>{getDateLabel() || "Alege perioada"}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="admin-calendar-chevron">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {showCalendar && (
                    <div className="admin-calendar-dropdown">
                      <DayPicker
                        mode="range"
                        selected={dateRange}
                        onSelect={setDateRange}
                        locale={ro}
                        numberOfMonths={1}
                        showOutsideDays
                        className="admin-rdp"
                      />
                      <button
                        type="button"
                        className="admin-calendar-done"
                        onClick={() => setShowCalendar(false)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Confirmă selecția
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Hour Select Dropdowns */}
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field">
                <label className="admin-calendar-label">Ora de început</label>
                <HourSelect
                  value={startHour}
                  onChange={(h) => setStartHour(h)}
                  placeholder="-- Selectează --"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field">
                <label className="admin-calendar-label">Ora de sfârșit</label>
                <HourSelect
                  value={endHour}
                  onChange={(h) => setEndHour(h)}
                  placeholder="-- Selectează --"
                />
              </div>
            </div>

            {/* Summary */}
            {(dateRange?.from || startHour || endHour) && (
              <div className="col-lg-12">
                <div className="admin-datetime-summary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span>{buildTimeInterval() || "—"}</span>
                </div>
              </div>
            )}

            {/* TinyMCE Editor */}
            <div className="col-lg-12">
              <div className="form-field">
                <Editor
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  initialValue={text}
                  onEditorChange={(value) => setText(value)}
                  init={{
                    height: 350,
                    menubar: false,
                    plugins: [
                      "advlist", "autolink", "lists", "link", "image", "charmap",
                      "preview", "anchor", "searchreplace", "visualblocks", "code",
                      "fullscreen", "insertdatetime", "media", "table", "code",
                      "help", "wordcount",
                    ],
                    toolbar:
                      "undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help",
                    content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                  }}
                />
              </div>
            </div>

            <div className="col-lg-12">
              <EventConfirmationFields
                pretitle={confirmationPretitle}
                title={confirmationTitle}
                text={confirmationText}
                onPretitleChange={setConfirmationPretitle}
                onTitleChange={setConfirmationTitle}
                onTextChange={setConfirmationText}
              />
            </div>

            {/* Image Upload */}
            <div className="col-lg-6 col-md-6 col-12">
              <div className="form-field">
                <label className="admin-calendar-label">Imagine copertă</label>
                {coverImage && !base64URL && (
                  <div style={{ width: 300, maxHeight: 300, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                    <img style={{ width: "100%" }} src={coverImage} alt="Cover" />
                  </div>
                )}
                {imagePreview && (
                  <div style={{ width: 300, maxHeight: 300, borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
                    <img style={{ width: "100%" }} src={URL.createObjectURL(imagePreview)} alt="preview" />
                  </div>
                )}
                <input
                  onChange={uploadImage}
                  type="file"
                  ref={inputRef}
                  accept="image/png, image/gif, image/jpeg"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="event-edit-modal__footer">
          <button className="event-edit-modal__btn-cancel" onClick={onClose}>Anulează</button>
          <button className="event-edit-modal__btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventEditModal;
